import { LitElement, html, css, PropertyValues } from 'lit';
import { property, customElement, query, state } from 'lit/decorators.js';
import { translateFn } from '../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../gzForm/formField';
import { GzInputWebComponent } from '../../gzInput/component/GzInputWebComponent';
import { GzButtonWebComponent } from '../../gzButton';
import { FormGroup } from '../../gzForm/formGroup/form-group';
import { Subscription } from 'rxjs';

export enum PromptTypes {
  IMAGE = 'image',
  LINK = 'link',
}
export type PromptType = PromptTypes.LINK | PromptTypes.IMAGE;

export enum PromptModes {
  CREATE = 'create',
  EDIT = 'edit',
}
export type PromptMode = PromptModes.CREATE | PromptModes.EDIT;

/**
 * @summary This component is used within the gz-markdown-editor component, as dialog content for inserting links and images .
 * @tag gz-md-editor-prompt-content
 * @fires submit - Custom event dispatched when the url and text inputs are filled and the confirm button is clicked.
 * @fires cancel - Custom event dispatched when the cancel button is clicked.
 */
@customElement('gz-md-editor-prompt-content')
export class GzMarkdownEditorPromptContentWebComponent extends LitElement {
  static get styles() {
    return css`
      .action-buttons {
        display: flex;
        justify-content: space-between;
        margin-top: calc(4 * var(--spacer));
      }
    `;
  }

  /**
   * The mode of the prompt, either create or edit.
   */
  @property({ type: String })
  mode: PromptMode;

  /**
   * The type of the prompt, either image or link.
   */
  @property({ type: String })
  type: PromptType;

  @property({ type: String })
  url: string;

  @property({ type: String })
  text: string;

  @query('#url')
  private urlInput: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#text')
  private textInput: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#confirmButton')
  confirmButton: GzButtonWebComponent;

  @state()
  private formGroup: FormGroup | null;

  @state()
  private subscription: Subscription;

  private buildFormGroup() {
    this.formGroup = new FormGroup({
      url: this.urlInput,
      text: this.textInput,
    });

    this.subscription = this.formGroup.valid$.subscribe({
      next: valid => {
        this.confirmButton.disabled = !valid;
      },
    });
  }

  private setFormData() {
    if (!this.formGroup) {
      return;
    }

    this.formGroup.value = {
      url: this.url,
      text: this.text,
    };
  }

  private onFormSubmit() {
    if (!this.formGroup) {
      return;
    }
    this.formGroup.unsubscribe();
    this.subscription.unsubscribe();
    this.dispatchEvent(new CustomEvent('submit', { detail: this.formGroup.value }));
  }

  private onFormCancel() {
    this.dispatchEvent(new CustomEvent('cancel'));
  }

  firstUpdated(changedProperties: PropertyValues) {
    super.firstUpdated(changedProperties);
    this.buildFormGroup();
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    this.setFormData();
  }

  disconnectedCallback() {
    this.subscription.unsubscribe();
    super.disconnectedCallback();
  }

  render() {
    return html`
      <gz-form-field label="URL" id="url">
        <gz-input gz-form-input type="text" placeholder="${translateFn('Enter the URL')}"></gz-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>
      <gz-form-field
        label="${translateFn('Text')}"
        id="text"
      >
        <gz-input
          gz-form-input
          type="text"
          placeholder="${translateFn('Enter the text')}"
        ></gz-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>

      <div class="action-buttons">
        <gz-button id="confirmButton" @click="${this.onFormSubmit}">${translateFn('Add')}</gz-button>
        <gz-button type="outline" color="secondary" @click="${this.onFormCancel}">${translateFn('Cancel')}</gz-button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-md-editor-prompt-content': GzMarkdownEditorPromptContentWebComponent;
  }
}
